import { useCallback, useRef, useState } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import type { ProcessDetailDto } from '@api/processes';

/**
 * Discriminated identifier for the at-most-one dialog the side panel's
 * kebab can open. Tracking the open state as a single union (rather than
 * five independent booleans) is what guarantees only one dialog mounts
 * even if a rapid click sequence fires multiple openers.
 *
 * `'duplicate'` and `'duplicate-confirm'` share a single submit handler
 * (`onDuplicateSubmit`) but render two different dialogs:
 *   - `'duplicate'`: the side-panel kebab's name-the-copy dialog (input
 *     field, user picks the new name)
 *   - `'duplicate-confirm'`: the canvas-toolbar's "Duplicate & edit"
 *     confirmation dialog (no input, auto-named, design-spec copy)
 */
export type SidePanelDialog =
  | 'details'
  | 'rename'
  | 'duplicate'
  | 'duplicate-confirm'
  | 'send-copy'
  | 'delete';

/**
 * Subset of `useProcessActions`'s return value that the kebab handlers
 * need. Defined as a structural type so the unit test can hand the hook
 * a tiny mock without spinning up React Query.
 */
export interface ProcessActionsApi {
  duplicate: (vars: {
    processId: string;
    body: { newName: string };
  }) => Promise<ProcessDetailDto>;
  remove: (processId: string) => Promise<void>;
}

export interface UseProcessSidePanelDialogsParams {
  processId: string | undefined;
  detailName: string | undefined;
  processActions: ProcessActionsApi;
  navigate: NavigateFunction;
  showSuccess: (msg: string) => void;
  showError: (msg: string) => void;
  /**
   * Routes through the editor's drift-aware rename pipeline (typically
   * the `onCommitRename` from {@link useProcessRename}). The kebab's
   * Rename dialog calls this on submit and closes itself when it resolves true.
   */
  onCommitRename: (newName: string) => Promise<boolean>;
}

export interface UseProcessSidePanelDialogsResult {
  openDialog: SidePanelDialog | null;
  closeDialog: () => void;
  /** Returns a handler that opens the named dialog when invoked. */
  openDialogFor: (which: SidePanelDialog) => () => void;
  onRenameSubmit: (newName: string) => Promise<void>;
  onDuplicateSubmit: (newName: string) => Promise<void>;
  onSendCopySubmit: (emailAddress: string) => void;
  onDeleteConfirm: () => Promise<void>;
}

/**
 * Owns the kebab-menu dialog state machine and the four user-action
 * handlers (rename, duplicate, send-copy, delete) it dispatches. Pulled
 * out of `ProcessEditorPage` so the page's cognitive complexity stays
 * bounded — the four try/catch + ternary branches now live here.
 *
 * Behaviour notes:
 *
 *   - **Rename** delegates to the injected `onCommitRename` (drift-aware
 *     auto-save flush). On success we close the dialog; on failure it stays
 *     open and `onCommitRename` itself surfaces the snackbar.
 *   - **Duplicate** spawns a new draft and navigates the user to it. On
 *     failure the dialog stays mounted so the user can retry.
 *   - **Send copy** is a UI-only stub (no backend yet) — it mirrors the
 *     design's prototype with a success snackbar keyed to the recipient.
 *   - **Delete** is fired by the {@link ConfirmDialog}; on success we
 *     close the dialog and navigate back to the gallery.
 */
export const useProcessSidePanelDialogs = ({
  processId,
  detailName,
  processActions,
  navigate,
  showSuccess,
  showError,
  onCommitRename,
}: UseProcessSidePanelDialogsParams): UseProcessSidePanelDialogsResult => {
  const [openDialog, setOpenDialog] = useState<SidePanelDialog | null>(null);
  const closeDialog = useCallback(() => setOpenDialog(null), []);
  // Cache the per-dialog opener so repeated calls with the same key
  // return the same function reference — keeps consumers' {@code
  // React.memo} comparisons cheap.
  const openersRef = useRef<Map<SidePanelDialog, () => void>>(new Map());
  const openDialogFor = useCallback((which: SidePanelDialog) => {
    let opener = openersRef.current.get(which);
    if (!opener) {
      opener = () => setOpenDialog(which);
      openersRef.current.set(which, opener);
    }
    return opener;
  }, []);

  const onRenameSubmit = useCallback(
    async (newName: string) => {
      if (await onCommitRename(newName)) {
        setOpenDialog(null);
      }
    },
    [onCommitRename],
  );

  const onDuplicateSubmit = useCallback(
    async (newName: string) => {
      if (!processId) return;
      try {
        const dup = await processActions.duplicate({
          processId,
          body: { newName },
        });
        setOpenDialog(null);
        showSuccess(`Duplicated as “${dup.name}”.`);
        navigate(`/processes/${encodeURIComponent(dup.processId)}`);
      } catch (e) {
        const message =
          e instanceof Error ? e.message : 'Failed to duplicate process.';
        showError(message);
      }
    },
    [processId, processActions, navigate, showSuccess, showError],
  );

  const onSendCopySubmit = useCallback(
    (emailAddress: string) => {
      setOpenDialog(null);
      showSuccess(
        `Copy of “${detailName ?? 'this process'}” sent to ${emailAddress}.`,
      );
    },
    [detailName, showSuccess],
  );

  const onDeleteConfirm = useCallback(async () => {
    if (!processId) return;
    try {
      await processActions.remove(processId);
      setOpenDialog(null);
      showSuccess(`Deleted “${detailName ?? 'process'}”.`);
      navigate('/processes');
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Failed to delete process.';
      showError(message);
    }
  }, [
    processId,
    processActions,
    navigate,
    detailName,
    showSuccess,
    showError,
  ]);

  return {
    openDialog,
    closeDialog,
    openDialogFor,
    onRenameSubmit,
    onDuplicateSubmit,
    onSendCopySubmit,
    onDeleteConfirm,
  };
};
